import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Tag } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetVwMatGapRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { ColumnsType } from 'antd/es/table';
import type { VwMatGapRequestItemOutstanding } from '../../../types';
import { fetchSqlData } from '../../../services/api';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatGapIssueRequestDetails() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation() as any;
  const idParam = params?.id as string | undefined;
  const refNoFromState = location?.state?.ref_no as string | undefined;

  const [refNo, setRefNo] = useState<string | undefined>(refNoFromState);
  const [headerRecord, setHeaderRecord] = useState<any | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    const resolveHeader = async () => {
      try {
        let header: any | undefined = undefined;
        // 1) If route param is provided and numeric, fetch by id
        if (idParam && /^\d+$/.test(idParam)) {
          const resById = await fetchSqlData('mat_gap_issue_requests', 'id', Number(idParam));
          const rowsById = Array.isArray(resById) ? resById : (resById?.data ?? []);
          header = rowsById?.[0];
        }
        // 2) If not found and we have a ref_no from state, fetch by ref_no
        if (!header && refNoFromState) {
          const resByRef = await fetchSqlData('mat_gap_issue_requests', 'ref_no', String(refNoFromState));
          const rowsByRef = Array.isArray(resByRef) ? resByRef : (resByRef?.data ?? []);
          header = rowsByRef?.[0];
        }
        // 3) If not found and idParam looks like a non-numeric ref_no, try by ref_no
        if (!header && idParam && !/^\d+$/.test(idParam)) {
          const resByRef2 = await fetchSqlData('mat_gap_issue_requests', 'ref_no', String(idParam));
          const rowsByRef2 = Array.isArray(resByRef2) ? resByRef2 : (resByRef2?.data ?? []);
          header = rowsByRef2?.[0];
        }
        if (mounted && header) {
          setHeaderRecord(header);
          if (!refNoFromState && header?.ref_no) setRefNo(String(header.ref_no));
          if (refNoFromState && !refNo) setRefNo(String(refNoFromState));
        } else if (mounted && refNoFromState && !refNo) {
          // At least set the refNo to be able to filter the view
          setRefNo(String(refNoFromState));
        }
      } catch (e) {
        if (mounted && refNoFromState && !refNo) setRefNo(String(refNoFromState));
      }
    };
    resolveHeader();
    return () => { mounted = false; };
  }, [idParam, refNoFromState, refNo]);

  const { data = [], isLoading, refetch } = useGetVwMatGapRequestItemOutstanding() as any;
  const rows: VwMatGapRequestItemOutstanding[] = Array.isArray(data) ? data : (data?.data ?? []);

  // Always refresh outstanding on page entry to reflect latest partial issues
  useEffect(() => {
    if (typeof refetch === 'function') refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!refNo) return [] as VwMatGapRequestItemOutstanding[];
    const key = String(refNo).trim();
    return (rows || []).filter(r => String(r.ref_no || '').trim() === key);
  }, [rows, refNo]);

  const header = filtered[0];
  // Header-level fields come from mat_gap_issue_requests (headerRecord)
  const statusVal = (headerRecord?.status) as string | undefined;
  const statusTag = statusVal ? (
    <Tag color={
      statusVal === 'pending' ? 'gold' :
      statusVal === 'approved' ? 'blue' :
      statusVal === 'partially_issued' ? 'purple' :
      statusVal === 'issued' ? 'green' :
      statusVal === 'rejected' ? 'red' : 'default'
    }>{statusVal}</Tag>
  ) : null;

  // Use any[] to allow custom exportValue on columns
  const columns: any[] = [
    { title: 'Item ID', dataIndex: 'item_id', key: 'item_id', width: 100 },
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 300, ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 120, align: 'right' },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'issued_qty', width: 120, align: 'right' },
    { title: 'Outstanding', dataIndex: 'outstanding_qty', key: 'outstanding_qty', width: 130, align: 'right' },
    { title: 'Requested WH', dataIndex: 'requested_wh_name', key: 'requested_wh_name', width: 200,
      render: (_: any, r: VwMatGapRequestItemOutstanding) => {
        const name = r.requested_wh_name;
        const id = r.requested_wh_id;
        if (name) return name;
        if (id != null) return String(id);
        return '-';
      },
      exportValue: (r: VwMatGapRequestItemOutstanding) => r.requested_wh_name ?? (r.requested_wh_id != null ? String(r.requested_wh_id) : '-')
    },
  ];


  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Issue Request Details</Title>
              <Text type="secondary">Ref No: {refNo ?? '...'}</Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Header info in one line */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space size={24} wrap>
            <Text><strong>Ref No:</strong> {headerRecord?.ref_no || refNo || ''}</Text>
            <Text><strong>Request Date:</strong> {headerRecord?.req_date ? dayjs(headerRecord.req_date).format('YYYY-MM-DD') : ''}</Text>
            <span><Text strong>Status:</Text> {statusTag}</span>
            <Text><strong>Requester (Code):</strong> {headerRecord?.requester || ''}</Text>
            <Text><strong>Requester Name:</strong> {headerRecord?.requester_name || ''}</Text>
          </Space>
        </Col>
      </Row>

      {/* Notes in second line */}
      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Text><strong>Notes:</strong> {headerRecord?.notes || ''}</Text>
        </Col>
      </Row>

      <StandardTable<VwMatGapRequestItemOutstanding>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => String(r.item_id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
